import React from "react";
import { TextField, FormControl } from "@mui/material";
import { useField, ErrorMessage, useFormikContext } from "formik";
import PropTypes from "prop-types";
import FormikLabel from "./FormikLabel";
import { ErrorStyle } from "./ErrorStyle";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";

const FormikMonthYearPicker = ({
  name = "",
  label,
  width = "100%",
  required = false,
  minDate,
  maxDate,
  placeholder = "",
  disabled = false,
  pickerType = "month", // 'month' or 'year'
  onChange = (formattedValue) => {
    console.log(`Selected value for ${name}:`, formattedValue);
  },
  shouldDisableMonth,
  ...props
}) => {
  const [field, meta, helpers] = useField(name);
  const { setFieldError } = useFormikContext();
  const pickerViews = pickerType === "year" ? ["year"] : ["year", "month"];
  const formatString = pickerType === "year" ? "yyyy" : "yyyy-MM";

  const value = field.value ? new Date(field.value) : null; // Ensure value is a Date object

  const handleDateChange = (newValue) => {
    console.log(`Selected value for ${name}:`, newValue);
    if (newValue) {
      const formattedValue = format(newValue, formatString);
      onChange(formattedValue);
      helpers.setValue(formattedValue);
      setFieldError(name, undefined);
    } else {
      onChange("");
      helpers.setValue("");
    }
  };

  return (
    <FormControl
      key={name}
      variant="outlined"
      sx={{
        // marginBottom: 2,
        width,
      }}
    >
      <FormikLabel text={label} required={required} />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          views={pickerViews}
          openTo="month"
          value={value}
          onChange={handleDateChange}
          minDate={minDate}
          maxDate={maxDate}
          disabled={disabled}
          shouldDisableMonth={shouldDisableMonth}
          slotProps={{
            textField: {
              fullWidth: true,
              id: name,
              name: name,
              variant: "outlined",
              placeholder,
              error: meta.touched && Boolean(meta.error),
              onBlur: () => helpers.setTouched(true),
              onClick: () => helpers.setTouched(true),
              sx: {
                ...props.sx,
                "& .MuiInputBase-input": {
                  padding: "10px",
                  height: "100%",
                  boxSizing: "border-box",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "8px",
                  borderWidth: "1px",
                },
              },
              InputLabelProps: { shrink: true },
            },
          }}
        />
      </LocalizationProvider>
      <ErrorMessage name={name} component="div" style={ErrorStyle} />
    </FormControl>
  );
};

// PropTypes for validation
FormikMonthYearPicker.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  width: PropTypes.string,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  pickerType: PropTypes.oneOf(["month", "year"]), // Allow either 'month' or 'year'
  onChange: PropTypes.func, // PropType for onChange function
};

export default FormikMonthYearPicker;
